import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type {
  EnvironmentView,
  ProjectModuleView,
  ReleaseDetail,
  ReleaseScope,
  ReleaseSummary,
  ReleaseTemplateSummary,
  ReleaseType,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import { RELEASE_TYPE_LABELS } from "./releaseVocabulary";

/**
 * Creating a release (§8).
 *
 * <p>Three fields, and that is the whole point. §8 insists the release page exists from the moment
 * the release does rather than once everything is known, so this asks only for what a release
 * cannot be without: what it is called, which version it is, and what kind of change it carries.
 * Owners, dates, environments and branches are all edited on the page afterwards, because that is
 * when anybody actually knows them.
 *
 * <p>A template is the fourth, optional, field (§54). Choosing one copies its prerequisites,
 * approvals, runbook, rollback plan and checks into the new release — a copy, so editing the
 * template later never touches what this release was held to (§52).
 */
export function ReleaseFormDrawer({
  projectId,
  open,
  onClose,
  onSaved,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onSaved: (created: ReleaseDetail | null) => void;
}) {
  const [releaseName, setReleaseName] = useState("");
  const [version, setVersion] = useState("");
  const [releaseType, setReleaseType] = useState<ReleaseType>("MINOR");
  const [templateId, setTemplateId] = useState("");
  const [environmentId, setEnvironmentId] = useState("");
  const [releaseScope, setReleaseScope] = useState<ReleaseScope>("PROJECT");
  const [moduleIds, setModuleIds] = useState<string[]>([]);
  /* Rolling a shared component out: take the plan from the release that shipped it. */
  const [copyFromReleaseId, setCopyFromReleaseId] = useState("");
  /* A local datetime string, which is what <input type="datetime-local"> speaks. */
  const [plannedReleaseAt, setPlannedReleaseAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  /*
   * §63: where it is going decides what it is judged against. Asked here rather than only
   * afterwards, because a release aimed nowhere is held to every gate — and somebody who has
   * just created one and sees twelve red checks has done nothing wrong.
   */
  const environments = useQuery({
    queryKey: ["release-environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
    enabled: open,
  });

  /* Which modules this project is built from, and what it runs — the choices for a module or
     component release, and where their current version comes from. */
  const held = useQuery({
    queryKey: ["project-modules", projectId],
    queryFn: () => api.get<ProjectModuleView[]>(`/api/v1/projects/${projectId}/modules`),
    enabled: open,
  });

  const eligible = (held.data ?? []).filter((row) =>
    releaseScope === "COMPONENT"
      ? row.module.moduleType.sharedVersion
      : !row.module.moduleType.sharedVersion,
  );
  /* The first named module is the primary: the header, the release list and template matching all
     want one name rather than a list. */
  const chosenModule =
    eligible.find((row) => row.module.id === moduleIds[0]) ?? null;

  /* Asked with the scope, so the runbook for a component release of Accounting is offered ahead
     of "standard release" rather than lost among templates that do not apply. */
  const templates = useQuery({
    queryKey: ["release-templates", "usable", releaseType, releaseScope, moduleIds[0]],
    queryFn: () =>
      api.get<ReleaseTemplateSummary[]>(
        `/api/v1/release-templates?releaseType=${releaseType}&releaseScope=${releaseScope}`
          + (moduleIds[0] ? `&moduleId=${moduleIds[0]}` : ""),
      ),
    enabled: open,
  });

  /* Only ones with an activated version — a template nobody published has nothing to copy. */
  const usable = (templates.data ?? []).filter(
    (template) => template.activeVersionNumber != null,
  );

  /* Releases that shipped the chosen component, anywhere. A rollout takes its plan from the
     release that actually shipped the thing. */
  const sourceReleases = useQuery({
    queryKey: ["component-source-releases", moduleIds[0]],
    queryFn: () =>
      api.get<ReleaseSummary[]>("/api/v1/releases?size=100"),
    enabled: open && releaseScope === "COMPONENT" && moduleIds.length > 0,
  });

  /* A template that names an environment aims the release; the field then shows what it chose. */
  const templateEnvironment =
    usable.find((template) => template.id === templateId)?.environment ?? null;


  useEffect(() => {
    if (open) {
      setReleaseScope("PROJECT");
      setModuleIds([]);
      setCopyFromReleaseId("");
    setPlannedReleaseAt("");
      setReleaseName("");
      setVersion("");
      setReleaseType("MINOR");
      setTemplateId("");
      setEnvironmentId("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = templateId === ""
        ? await api.post<ReleaseDetail>(
            `/api/v1/projects/${projectId}/releases`,
            {
              releaseName: releaseName.trim(),
              version: version.trim(),
              releaseType,
              targetEnvironmentId: environmentId === "" ? null : environmentId,
              releaseScope,
              moduleIds: releaseScope === "PROJECT" ? [] : moduleIds,
              copyFromReleaseId: copyFromReleaseId === "" ? null : copyFromReleaseId,
              plannedReleaseAt: new Date(plannedReleaseAt).toISOString(),
            },
          )
        : await api.post<ReleaseDetail>("/api/v1/releases/from-template", {
            projectId,
            releaseName: releaseName.trim(),
            version: version.trim(),
            releaseType,
            templateId,
            plannedReleaseAt: new Date(plannedReleaseAt).toISOString(),
          });
      void queryClient.invalidateQueries({ queryKey: ["releases", projectId] });
      onSaved(created);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not create the release.");
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = releaseName.trim() !== "" && version.trim() !== ""
    && plannedReleaseAt !== ""
    && (releaseScope === "PROJECT" || moduleIds.length > 0);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New release"
      subtitle="A version with agreed scope. The rest is filled in as you learn it."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Creating…" : "Create release"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {/*
          What this is a release OF, asked first because it changes what the version below means.
          Without the distinction the only honest way to ship one module is to cut a whole product
          release, which inflates the product number until it stops meaning anything.
        */}
        {(held.data ?? []).length > 0 && (
          <Field label="This is a release of" required>
            <Select
              value={releaseScope}
              onChange={(event) => {
                setReleaseScope(event.target.value as ReleaseScope);
                setModuleIds([]);
              }}
            >
              <option value="PROJECT">The whole product — every module</option>
              <option value="MODULE">One module of this project</option>
              <option value="COMPONENT">A shared component used by other projects too</option>
            </Select>
          </Field>
        )}

        {/* Several, not one: a project built from five ships two together often enough that a
            single choice is the wrong shape. Ticked in the order they matter — the first is the
            primary, which is the name the header and the release list show. */}
        {releaseScope !== "PROJECT" && (
          <Field
            label={releaseScope === "COMPONENT" ? "Components" : "Modules"}
            required
            hint="Tick everything this release covers. Each one gets its own line on the manifest."
          >
            <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
              {eligible.length === 0 ? (
                <p className="px-3 py-2 text-sm text-ink-500">
                  {releaseScope === "COMPONENT"
                    ? "This project has no shared components linked."
                    : "This project has no modules of its own linked."}
                </p>
              ) : (
                eligible.map((row) => (
                  <label
                    key={row.module.id}
                    className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-ink-50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-ink-300"
                      checked={moduleIds.includes(row.module.id)}
                      onChange={() =>
                        setModuleIds((now) =>
                          now.includes(row.module.id)
                            ? now.filter((id) => id !== row.module.id)
                            : [...now, row.module.id],
                        )
                      }
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-900">
                      {row.module.name}
                    </span>
                    <span className="tnum shrink-0 font-mono text-xs text-ink-500">
                      {row.currentVersion ? `on ${row.currentVersion}` : "no version yet"}
                    </span>
                  </label>
                ))
              )}
            </div>
          </Field>
        )}

        {releaseScope !== "PROJECT" && chosenModule && (
          <InlineAlert tone="info">
            {moduleIds.length === 1
              ? `The version below is ${chosenModule.module.name}'s, not the product's.`
              : `The version below applies to all ${moduleIds.length} — ${chosenModule.module.name} is the one the release is named after.`}{" "}
            This release does not change the product number.
          </InlineAlert>
        )}

        <Field
          label="Version"
          required
          hint={
            releaseScope === "PROJECT"
              ? "Whatever your team calls it — 2.1, R2026.08, August-Production."
              : "The module's new version — 3.9, 2.1."
          }
        >
          <Input
            autoFocus
            value={version}
            placeholder={chosenModule?.currentVersion ?? "2.1"}
            onChange={(event) => setVersion(event.target.value)}
          />
        </Field>

        <Field label="Name" required>
          <Input
            value={releaseName}
            placeholder="PR Blocks 2.1"
            onChange={(event) => setReleaseName(event.target.value)}
          />
        </Field>

        <Field
          label="Going out on"
          required
          hint={
            "Asked for now because everything that protects this date depends on there being "
            + "one — and because moving it later needs a reason and somebody's approval, while "
            + "setting the first one needs neither."
          }
        >
          <Input
            type="datetime-local"
            value={plannedReleaseAt}
            onChange={(event) => setPlannedReleaseAt(event.target.value)}
          />
        </Field>

        <Field
          label="Type"
          required
          hint="Drives which approvals and tests apply. Chosen, never guessed from the version."
        >
          <Select
            value={releaseType}
            onChange={(event) => {
              setReleaseType(event.target.value as ReleaseType);
              // The template list is filtered by type, so a template chosen for the old type
              // may not apply any more. Clearing it beats silently sending a mismatched id.
              setTemplateId("");
            }}
          >
            {Object.entries(RELEASE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Going to"
          hint="Decides what this release is judged against. Development asks almost nothing; production asks everything. Can be changed later."
        >
          <Select
            value={templateId !== "" && templateEnvironment ? templateEnvironment.id : environmentId}
            disabled={templateId !== "" && templateEnvironment != null}
            onChange={(event) => setEnvironmentId(event.target.value)}
          >
            <option value="">Not decided yet</option>
            {(environments.data ?? []).map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name}
                {environment.production ? " (production)" : ""}
              </option>
            ))}
          </Select>
        </Field>

        {templateId !== "" && templateEnvironment && (
          <p className="-mt-2 text-xs text-ink-500">
            Set by the {usable.find((one) => one.id === templateId)?.name} template. You can
            change it on the release afterwards.
          </p>
        )}

        {environmentId === "" && templateEnvironment == null && (
          <InlineAlert tone="info">
            Left undecided, the release is held to every gate — which is the safe reading, but
            reads as a wall of red on something nobody has done anything wrong with yet.
          </InlineAlert>
        )}

        {/* Where the plan comes from. A shared component is released once in its own project and
            rolled out to every customer that uses it — the preparation, runbook and checks come
            across unticked, and the new release names the one it came from. */}
        {releaseScope === "COMPONENT" && moduleIds.length > 0 && (
          <Field
            label="Take the plan from"
            hint="The component's own release. Everything comes across unticked and editable; its scope stays where it is, and this release links back to it."
          >
            <Select
              value={copyFromReleaseId}
              onChange={(event) => setCopyFromReleaseId(event.target.value)}
            >
              <option value="">Start from nothing</option>
              {(sourceReleases.data ?? []).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.releaseName} ({candidate.version}) — {candidate.projectName}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {usable.length > 0 && (
          <Field
            label="Template"
            hint="Its prerequisites, approvals, runbook and checks are copied in. Changing the template later never affects this release."
          >
            <Select
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
            >
              <option value="">Start from nothing</option>
              {usable.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.applicableModule ? ` — ${template.applicableModule.name}` : ""}
                  {` (V${template.activeVersionNumber})`}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
    </Drawer>
  );
}
